//Personicom.NT.Reporting: /src/app-state.tsx
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { throttle, debounce, isEqual } from 'lodash';
import { useHistory } from "react-router";
import { PageInfo, PaginationInfo, ReportRow, KeysetPaginatedList } from "./app-types";
import { useReportConfig, ReportConfigDiagnostic, ValidatedReportGridConfig } from "helpers/hooks";
import { getReportData, getReportDetails, getReportDownload, replaceItemDetails, normalizeReportRow } from "features/reporting/reporting-helpers";
import { IPayloadError, useCustomizations, useError } from "@personicom/customizations";
import { reportingLogger } from "helpers/reporting-logger";
import { getSearchParam } from "helpers/general-helpers";
import { FilterCondition, SortCondition } from "features/reporting/reporting-types";
import { encodeFilterConditions, decodeFilterConditions, encodeSortConditions, decodeSortConditions } from "features/reporting/reporting-helpers"
import { getUatDiagnosticsContext, reportUatDiagnostic } from "helpers/uat-diagnostics";


export const DEFAULT_PAGE_INFO: PageInfo = {
  number: 1,
  size: 25, // You can adjust this default page size if needed
};

/**
 * Applies incoming filter updates to the previous collection, returning the previous reference when
 * nothing changes so React state consumers do not churn unnecessarily.
 */
export const mergeFilters = (prevFilters: FilterCondition[], incomingFilters: FilterCondition[]): FilterCondition[] => {
  if (incomingFilters.length === 0) {
    return prevFilters;
  }

  let hasChanges = false;
  const mergedFilters = [...prevFilters];

  incomingFilters.forEach((newFilter) => {
    const existingIndex = mergedFilters.findIndex((existing) =>
      existing.id ? existing.id === newFilter.id : existing.columnField.toLowerCase() === newFilter.columnField.toLowerCase()
    );

    if (existingIndex !== -1) {
      if (!isEqual(mergedFilters[existingIndex], newFilter)) {
        mergedFilters[existingIndex] = newFilter;
        hasChanges = true;
      }
    } else {
      mergedFilters.push(newFilter);
      hasChanges = true;
    }
  });

  return hasChanges ? mergedFilters : prevFilters;
};

const summarizeFilterShape = (activeFilters: FilterCondition[] = []): string[] =>
  activeFilters.map((filter) => `${filter.columnField}:${filter.operatorValue}`);

const summarizeSortShape = (activeSorts: SortCondition[] = []): string[] =>
  activeSorts.map((sort) => `${sort.columnName}:${sort.direction}`);

// getStoredPlatformToken: reads an optional Entra access token staged by future platform SSO flows.
const getStoredPlatformToken = (): string | undefined => {
  try {
    return window.sessionStorage.getItem("personicom-platform-access-token") ?? undefined;
  } catch {
    return undefined;
  }
};

// Provider that will hold the reporting app state
export const AppStateProvider: React.FC = ({ children }) => {
  const [isWorking, setIsWorking] = useState(false);
  const [page, setPage] = useState<PageInfo>(DEFAULT_PAGE_INFO);
  const [items, setItems] = useState<ReportRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | undefined>(undefined);
  const [sorts, setSortState] = useState<SortCondition[]>([]);                            //Source of truth for sorting.
  const [filters, setFiltersState] = useState<FilterCondition[]>([]);                     //Source of truth for filters.
  const [selectedRow, setSelectedRowState] = useState<string | undefined>(undefined);
  const [isAppInitialized, setIsAppInitialized] = useState(false);
  const [areFiltersInitialized, setAreFiltersInitialized] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [hasPerformedInitialLoad, setHasPerformedInitialLoad] = useState(false);
  const [configDiagnostics, setConfigDiagnostics] = useState<ReportConfigDiagnostic[]>([]);

  const reportConfig = useReportConfig();
  const { client, subdomain } = useCustomizations();
  const { setError, clearError } = useError();
  let token = useMemo(() => getSearchParam("token", true, true) ?? undefined, []);
  const apiKey = useMemo(() => getSearchParam("apiKey", true, true) ?? undefined, []);
  const platformToken = useMemo(() => getSearchParam("platformToken", true, true) ?? getStoredPlatformToken(), []);
  const uatDiagnosticsContext = useMemo(() => getUatDiagnosticsContext(), []);
  const history = useHistory();

  const isFetching = useRef(false);         // Flag to prevent multiple fetches
  const pendingRequest = useRef(false);     // Track the fetch is out of date, and we need to refetch.

  const isPaginationResetRef = useRef(false);
  const previousFiltersRef = useRef<FilterCondition[] | null>(null);
  const previousSortsRef = useRef<SortCondition[] | null>(null);

  token = token ?? apiKey ?? platformToken;

  useEffect(() => {
    if (!uatDiagnosticsContext.enabled) {
      return;
    }

    reportUatDiagnostic('app_initialized', {
      client,
      subdomain,
      path: window.location.pathname,
      hasToken: Boolean(token),
      sessionCorrelationId: uatDiagnosticsContext.sessionCorrelationId,
    });
  }, [client, subdomain, token, uatDiagnosticsContext.enabled, uatDiagnosticsContext.sessionCorrelationId]);

  useEffect(() => {
    if (reportConfig?.diagnostics?.length) {
      setConfigDiagnostics(reportConfig.diagnostics);
    } else {
      setConfigDiagnostics([]);
    }
  }, [reportConfig]);

  // Helper function to update the URL query string with current state that affects the report.
  const updateUrlFromState = () => {
    if (!isAppInitialized) return;  // Don't update URL until initialized
    const filterString = encodeFilterConditions(filters);
    const sortString = encodeSortConditions(sorts);
    const rowId = selectedRow;

    const params: Record<string, string | undefined> = {
      filter: filterString || undefined,
      sort: sortString || undefined,
      rowid: rowId || undefined,
      page: page.number.toString(),
      pageSize: page.size.toString(),
    };

    const urlParams = new URLSearchParams(window.location.search);
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value === undefined) {
        urlParams.delete(key); // Remove the parameter if it's undefined
      } else {
        urlParams.set(key, value); // Update or add the parameter
      }
    });

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    history.replace(newUrl);
  };

  // Use effect to update the URL whenever filters, sorts, or row selection changes
  useEffect(() => {
    const handle = setTimeout(() => {
      updateUrlFromState();
    }, 300); // Debounce by 300ms to prevent excessive URL updates

    return () => clearTimeout(handle);
  }, [filters, sorts, selectedRow, page.number, page.size]);


  
  const applyDefaultSort = () => {
    if (!reportConfig) {
      reportingLogger.verbose('applyDefaultSort: Report config is not yet available', undefined, 'Reporting');
      return;
    }

    if (reportConfig?.defaultSort && reportConfig.defaultSort.length > 0) {
      const defaultSortConditions = reportConfig.defaultSort
        .sort((a, b) => a.ordinal - b.ordinal)
        .map((sortField) => ({
          columnName: sortField.field,
          direction: sortField.direction,
        }));
      setSortAction(defaultSortConditions);
    }
  };

  // Block the initial fetchData while setting up states from URL
  const initializeStateFromUrl = async () => {
    if (!reportConfig) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const filterString = urlParams.get("filter");
    const sortString = urlParams.get("sort");
    const rowId = urlParams.get("rowid");

    // Get page size and page number from URL
    const pageNumberFromUrl = parseInt(urlParams.get("page") || page.number.toString(), 10);
    const pageSizeFromUrl = parseInt(urlParams.get("pageSize") || page.size.toString(), 10);
    setPage({ number: pageNumberFromUrl, size: pageSizeFromUrl });

    if (filterString) {
      const decodedFilters = decodeFilterConditions(filterString);
      
      // If we got invalid filters (empty array from invalid format), don't set them
      // This allows the app to use its default filters instead
      if (decodedFilters.length > 0) {
        setFiltersAction(decodedFilters);
      } else if (filterString.length > 0) {
        // Invalid filter format detected - log and let defaults apply
        reportingLogger.verbose(
          'Invalid filter format in URL. Using default filters.',
          { filterString },
          'Reporting'
        );
      }
    }

    if (sortString) {
      const decodedSorts = decodeSortConditions(sortString);
      if (decodedSorts.length > 0) {
        setSortAction(decodedSorts);
      } else {
        // Entirely-invalid sort in the URL (e.g. a mangled pasted link) — show the
        // baseline report with the configured default sort instead of erroring.
        reportingLogger.verbose('Invalid sort format in URL. Using default sort.', { sortString }, 'Reporting');
        applyDefaultSort();
      }
    } else {
      applyDefaultSort();
    }

    if (rowId) {
      selectRowAction(rowId);
    }

    // Mark the initialization as complete
    if (!isAppInitialized) {
      setIsAppInitialized(true);
    }
  };

  // Use effect to parse URL on initial load and set state accordingly
  useEffect(() => {
    const init = async () => {
      await initializeStateFromUrl();
    };

    init(); // Invoke the async function

  }, [reportConfig]);


  // Define throttled version of fetchData
  const throttledFetchData = useCallback(
    throttle(() => {
      fetchData();
    }, 1500, { leading: true, trailing: false }), // 1.5s throttle
    [sorts, filters, page.number, page.size, token, pagination ]    // ensure the function is recreated if these values change
  );

  const debouncedFetchData = debounce(() => {
    fetchData();
  }, 1500); // 1.5s debounce

  // Perform initial data load when app is fully initialized
  useEffect(() => {
    if (isAppInitialized && areFiltersInitialized && !hasPerformedInitialLoad) {
      reportingLogger.verbose('Performing initial data load', undefined, 'Reporting');
      setHasPerformedInitialLoad(true);
      setIsDirty(false); // Ensure not dirty during initial load
      fetchData(); // Initial load only
    }
  }, [isAppInitialized, areFiltersInitialized, hasPerformedInitialLoad]);

  // Mark as dirty when filters or sorts change (don't auto-refresh after initial load)
  useEffect(() => {
    if (!isAppInitialized || !areFiltersInitialized || !hasPerformedInitialLoad) return;
    
    // Check if filters or sorts actually changed (not just initial setup)
    const filtersChanged = previousFiltersRef.current !== null && !isEqual(previousFiltersRef.current, filters);
    const sortsChanged = previousSortsRef.current !== null && !isEqual(previousSortsRef.current, sorts);
    
    if (sortsChanged && !filtersChanged) {
      reportingLogger.verbose(
        'Sort changed - resetting pagination/bookmark and refreshing data immediately',
        { sortsChanged, previousPage: page.number },
        'Reporting'
      );
      reportUatDiagnostic('sort_changed_refreshing', {
        previousPage: page.number,
        sorts: summarizeSortShape(sorts),
      });
      setIsDirty(false);
      isPaginationResetRef.current = true;
      setPagination(undefined);

      if (page.number !== 1) {
        setPage((previousPage) => (
          previousPage.number === 1
            ? previousPage
            : { ...previousPage, number: 1 }
        ));
      } else {
        fetchData();
      }
    } else if (filtersChanged || sortsChanged) {
      reportingLogger.verbose('Filters or sorts changed - marking dirty', { filtersChanged, sortsChanged }, 'Reporting');
      reportUatDiagnostic('state_marked_dirty', {
        filtersChanged,
        sortsChanged,
        filters: summarizeFilterShape(filters),
        sorts: summarizeSortShape(sorts),
      });
      setIsDirty(true);
    }
    
    // Update refs with current values
    previousFiltersRef.current = filters;
    previousSortsRef.current = sorts;
    // Note: We do NOT call fetchData here - user must click Refresh
  }, [filters, sorts, isAppInitialized, areFiltersInitialized, hasPerformedInitialLoad, page.number]);

  // Use effect to fetch data when page changes (preserve pagination)
  useEffect(() => {
    if (!isAppInitialized || !areFiltersInitialized) return;
    isPaginationResetRef.current = false; // Don't reset pagination for page navigation
    fetchData(); // Direct fetch, no debounce for page changes
  }, [page.number, page.size]);


  const fetchData = async (): Promise<boolean> => {
    // Note: Parameter change checking removed - useEffect dependencies handle when to call this
    // The page.number/page.size useEffect ensures we fetch when pagination changes
    // Manual refresh triggers when filters/sorts change (via refresh button)

    if (isFetching.current) {
      pendingRequest.current = true; // Set the pending request flag
      reportingLogger.verbose('app-state: fetchData is currently blocked by another fetch request. pendingRequest set to true.', undefined, 'Reporting');
      return false; // Return false to indicate fetch was not started
    }

    // If dirty, reset pagination before fetching
    if (isDirty) {
      isPaginationResetRef.current = true;
      setPagination(undefined);
    }

    // Log whether this is initial load or user-triggered
    const loadType = !hasPerformedInitialLoad ? 'Initial Data Load' : (isDirty ? 'User Refresh' : 'Pagination');
    reportingLogger.verbose(`fetchData: ${loadType}`, { isDirty, hasPerformedInitialLoad }, 'Reporting');

    isFetching.current = true; // Set the lock
    setIsWorking(true); // Let the GUI know we're fetching.
    clearError();

    const requestStartedAt = Date.now();
    const requestCorrelationId = `${uatDiagnosticsContext.sessionCorrelationId}-${requestStartedAt}`;
    reportUatDiagnostic('report_request_started', {
      requestCorrelationId,
      pageNumber: page.number,
      pageSize: page.size,
      isDirty,
      shouldUseBookmark: !isPaginationResetRef.current && page.number > 1,
      hasBookmark: !!pagination?.encodedKeysetBookmark,
      filterShape: summarizeFilterShape(filters),
      sortShape: summarizeSortShape(sorts),
    });

    try {
      // Page 1 should always be fetched as an initial query to avoid applying stale keyset boundaries.
      const shouldSendBookmark = !isPaginationResetRef.current && page.number > 1;
      const bookmarkToSend = shouldSendBookmark ? pagination?.encodedKeysetBookmark : undefined;
      reportingLogger.verbose(
        `fetchData: Sending request - page=${page.number}, isPaginationReset=${isPaginationResetRef.current}, shouldSendBookmark=${shouldSendBookmark}, bookmark=${bookmarkToSend ? 'present' : 'none'}`,
        { pageNumber: page.number, pageSize: page.size, isPaginationReset: isPaginationResetRef.current, shouldSendBookmark, hasBookmark: !!bookmarkToSend },
        'Reporting'
      );
      
      const data: KeysetPaginatedList<ReportRow> | IPayloadError = await getReportData(
        client,
        subdomain,
        page.number,
        page.size,
        sorts,
        filters,
        token,
        bookmarkToSend,
        requestCorrelationId
      );

      if (!data || typeof data !== 'object') {
        reportUatDiagnostic('report_request_invalid_payload', {
          requestCorrelationId,
          durationMs: Date.now() - requestStartedAt,
        });
        setError({
          title: "Error Fetching Data",
          detail: "The reporting API returned an empty or invalid payload.",
        });
      } else if ((data as IPayloadError).isError) {
        const error = data as IPayloadError;
        reportingLogger.error('API returned error:', error.message, 'Reporting');
        reportUatDiagnostic('report_request_api_error', {
          requestCorrelationId,
          durationMs: Date.now() - requestStartedAt,
          status: error.status,
          title: error.title,
          message: error.message,
        });
        
        // Build detailed error message with context
        let detailMessage = error.message;
        if (error.details && typeof error.details === 'object') {
          const detailsObj = error.details as Record<string, any>;
          if (detailsObj.rawResponse) {
            detailMessage += ` (Response: ${String(detailsObj.rawResponse).substring(0, 100)})`;
          }
        }
        
        setError({
          title: error.title || "Error Fetching Data",
          detail: detailMessage,
          errors: error.errors,
        });
      } else if ((data as KeysetPaginatedList<ReportRow>).items) {
        const payload = data as KeysetPaginatedList<ReportRow>;
        processPayload(data as KeysetPaginatedList<ReportRow>);
        reportUatDiagnostic('report_request_succeeded', {
          requestCorrelationId,
          durationMs: Date.now() - requestStartedAt,
          pageNumber: payload.pageNumber,
          pageSize: payload.pageSize,
          hasMore: payload.hasMore,
          itemCount: payload.items?.length ?? 0,
          totalCount: payload.totalCount,
        });
        setIsDirty(false); // Clear dirty flag after successful fetch
      } else {
        reportUatDiagnostic('report_request_missing_items', {
          requestCorrelationId,
          durationMs: Date.now() - requestStartedAt,
        });
        setError({
          title: "Error Fetching Data",
          detail: "The reporting API payload is missing expected result items.",
        });
      }
      return true;
    } catch (error: any) {
      reportingLogger.error("Unexpected Exception during fetch:", error, 'Reporting');
      reportUatDiagnostic('report_request_exception', {
        requestCorrelationId,
        durationMs: Date.now() - requestStartedAt,
        message: error?.message ?? String(error),
        status: error?.status,
      });
      
      // Extract meaningful error information - handle IPayloadError structure
      const errorTitle = error?.title || "Fetch Error";
      let errorDetail = error?.message || error?.toString() || "An unexpected error occurred while fetching data.";
      
      // Add additional context if available
      if (error?.details) {
        if (typeof error.details === 'string') {
          errorDetail += ` Details: ${error.details}`;
        } else if (typeof error.details === 'object') {
          const detailsObj = error.details as Record<string, any>;
          if (detailsObj.parseError) {
            errorDetail += ` (JSON Parse Error: ${detailsObj.parseError})`;
          }
          if (detailsObj.rawResponse) {
            errorDetail += ` (Raw Response: ${String(detailsObj.rawResponse).substring(0, 100)})`;
          }
        }
      }
      
      if (error?.status) {
        errorDetail += ` (Status: ${error.status})`;
      }
      
      setError({ 
        title: errorTitle, 
        detail: errorDetail,
        errors: error?.errors
      });
      return false;
    } finally {
      setIsWorking(false);
      isFetching.current = false; // Release the lock after the fetch
      if (pendingRequest.current === true) {
        reportingLogger.verbose('app-state: fetchData: pendingRequest is true; triggering another fetch.', undefined, 'Reporting');
        pendingRequest.current = false;
        fetchData(); // Trigger the pending request
      }
    }
  };

  const processPayload = (data: KeysetPaginatedList<ReportRow>) => {
    // Prefer API total count but keep prior value when the service omits it or sends a stale lower number on later pages
    const previousTotal = pagination?.totalCount ?? 0;
    const incomingTotal = data.totalCount ?? undefined;
    const preservedTotalCount = (() => {
      if (incomingTotal === undefined) {
        return previousTotal;
      }

      if (!isPaginationResetRef.current && previousTotal > 0) {
        return Math.max(incomingTotal, previousTotal);
      }

      return incomingTotal;
    })();

    const newPagination: PaginationInfo = {
      pageNumber: data.pageNumber,
      totalCount: preservedTotalCount,
      // Calculate total pages only when we have a count; otherwise stick with previous value
      totalPages: preservedTotalCount > 0 ? Math.ceil(preservedTotalCount / data.pageSize) : pagination?.totalPages ?? 0,
      hasNextPage: data.hasMore,
      hasPreviousPage: data.pageNumber > 1,
      encodedKeysetBookmark: data.nextCursor,
    };

    const normalizedItems = (data.items ?? []).map(item => normalizeReportRow(item, reportConfig));

    setPagination(newPagination);
    setPage({ number: data.pageNumber || 1, size: data.pageSize });
    setItems(normalizedItems);
  };

  const fetchItem = async (renderId: string) => {
    setIsWorking(true);
    clearError();
    try {
      const itemEvents = await getReportDetails(client, subdomain, renderId, token);
      const newItems = replaceItemDetails(items, renderId, itemEvents);
      if (newItems && newItems !== items) setItems(newItems);
    } catch (error: any) {
      setError({
        title: "Error Fetching Details",
        detail: error?.message || "Unable to refresh the selected report details.",
      });
    } finally {
      setIsWorking(false);
    }
  };

  const exportData = async () => {
    setIsWorking(true);
    clearError();

    try {
      const success = await getReportDownload(client, subdomain, sorts, filters, token);
      if (!success) {
        setError({ title: "Export Error", detail: "Error downloading the report." });
      }
    } catch (error) {
      setError({ title: "Export Error", detail: "Error during file export." });
    } finally {
      setIsWorking(false);
    }
  };

  // Helper function to set sort state
  const setSortAction = (sortConditions: SortCondition[] | null) => {
    if (isEqual(sortConditions, sorts)) {
      return;
    }

    if (sortConditions && sortConditions.length > 0) {
      setSortState(sortConditions);
    } else {
      setSortState([]);
    }
  };

  // Add or update a single filter using setFiltersAction.
  const setFilterAction = (newFilter: FilterCondition) => {
    setFiltersAction([newFilter]);
  };

  // Helper function to merge multiple filters at once and handle removals
  /**
   * Adds or updates the provided filters while avoiding redundant state updates that would trigger loops.
   */
  const setFiltersAction = (newFilters: FilterCondition[]) => {
    setFiltersState((prevFilters) => mergeFilters(prevFilters, newFilters));
  };

  
  const removeFiltersAction = (filtersToRemove: FilterCondition[]) => {
    const remainingFilters = filters.filter(
      (filter) => !filtersToRemove.some((toRemove) => toRemove.id === filter.id)
    );
    setFiltersState(remainingFilters);
  };


  // Helper function to select a row
  const selectRowAction = (rowId?: string) => {
    if (!rowId) {
      setSelectedRowState(undefined); // Clear the selected row
    } else {
      setSelectedRowState(rowId); // Update selected row state
    }
  };

  // Actions for changing page and page size
  const changePage = (newPage: number) => {
    setPage({ number: newPage, size: page.size });
  };

  const changePageSize = (newSize: number) => {
    setPage({ number: 1, size: newSize });
  };

  
  /// ========================================
  ///       Refresh Action
  /// ========================================
  const refresh = async () => {
    await fetchData(); // Direct fetch when user clicks Refresh
  };

  const reset = async () => {
  
    // Clear filters and sorts
    setFiltersState([]);
    applyDefaultSort();    
  };

  const setFiltersInitialized = (val: boolean) => setAreFiltersInitialized(val);

  const theContext: IAppState = {
    isWorking,
    isAppInitialized,
    areFiltersInitialized,
    isDirty,
    page,
    pagination,
    items,
    selectedRow,
    reportConfig,
    configDiagnostics,
    sorts,
    filters,
    actions: {
      reset,
      refresh,
      setIsFetching: setIsWorking,
      removeFiltersAction,
      setFiltersInitialized : setFiltersInitialized,
      setPage,
      changePage,
      changePageSize,
      setSortAction,
      setFilterAction,
      setFiltersAction,
      fetchItem,
      exportData,
      setSelectedRow: selectRowAction,
    },
    token,
  };

  return <AppStateContext.Provider value={theContext}>{children}</AppStateContext.Provider>;
};

interface IAppState {
  isWorking: boolean;
  isAppInitialized: boolean;
  areFiltersInitialized: boolean,
  isDirty: boolean;
  page: PageInfo;
  pagination?: PaginationInfo;
  items?: any[];
  selectedRow?: any;
  reportConfig?: ValidatedReportGridConfig;
  configDiagnostics: ReportConfigDiagnostic[];
  sorts?: SortCondition[];
  filters?: FilterCondition[];
  actions: {
    reset: () => void;
    refresh: () => void;
    setIsFetching: (val: boolean) => void;
    setFiltersInitialized: (val: boolean) => void; 
    setPage: (val: PageInfo) => void;
    changePage: (val: number) => void;
    changePageSize: (val: number) => void;
    setSortAction: (val: SortCondition[]) => void;
    removeFiltersAction: (val: FilterCondition[]) => void;
    setFilterAction: (val: FilterCondition) => void;
    setFiltersAction: (val: FilterCondition[]) => void;
    fetchItem: (renderId: string) => void;
    exportData: () => void;
    setSelectedRow: (val: any) => void;
  };
  token?: string;
}

const defaultState: IAppState = {
  isWorking: false,
  isAppInitialized: false,
  areFiltersInitialized: false,
  isDirty: false,
  page: DEFAULT_PAGE_INFO,
  items: [],
  configDiagnostics: [],
  actions: {
    reset: () => { },
    refresh: () => { },
    setIsFetching: () => { },
    setFiltersInitialized : () => { },
    setPage: () => { },
    changePage: () => { },
    changePageSize: () => { },
    setSortAction: () => { },
    removeFiltersAction: () => { },
    setFilterAction: () => { },
    setFiltersAction: () => { },
    fetchItem: () => { },
    exportData: () => { },
    setSelectedRow: () => { },
  },
  token: undefined,
};


export const AppStateContext = React.createContext(defaultState);

